import { Injectable } from "@angular/core";
import { RemoteRepositoryInterface } from "../../remote/remote.interface";
import { ListInfo } from "../../entities/listinfo.model";
import { NurseryRepositoryInterface } from "./nursery.interface";
import { NurseryModel } from "../../entities/nursery.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { logData } from "../../../crossconcern/helpers/generic/generic.helper";
import { GetPayload } from "../../../crossconcern/types/payload.types";
import { PolygonType } from "../../../crossconcern/types/polygon.type";
import { PolygonModel } from "../../entities/polygon.model";
import { PolygonList } from "../../entities/polygonlist.model";
import { ShortcutList } from "../../entities/shortcutlist.model";
import { ShortcutType } from "../../../crossconcern/types/shortcut.type";
import { ShortcutModel } from "../../entities/shortcut.model";

@Injectable()
export class NurseryRepository implements NurseryRepositoryInterface {

    constructor(private remoteRepository: RemoteRepositoryInterface) {}

    public getNursery(uuid: string): Observable<NurseryModel> {
        const url = "nurseries/" + uuid;
        return this.remoteRepository.get(url)
            .pipe(
                map((data) => new NurseryModel(data as JSON))
            );
    }

    public getNurseries(visible: boolean, pageSize: number, page: number): Observable<[ListInfo, NurseryModel[]]> {
        let url = "nurseries";
        if(pageSize != null && page != null) {
            url += "?size=" + pageSize + "&page=" + page;
            if (visible != null) {
                url += "&search=visible:" + visible;
            }
        }
        if (visible != null) {
            url += "&search=visible:" + visible;
        }

        return this.remoteRepository.get(url)
            .pipe(
                map((data: GetPayload) => {
                    const items: NurseryModel[] = [];
                    for (const item of data.content) {
                        logData(item);
                        items.push(new NurseryModel(item));
                    }
                    const listInfo = new ListInfo(data);
                    return [
                        listInfo, items
                    ];
                })
            );
    }

    public addNursery(body: unknown): Observable<NurseryModel> {
        return this.remoteRepository.post("nurseries/", body)
            .pipe(
                map((data) => new NurseryModel(data as JSON))
            );
    }

    public editNursery(id: string, body: unknown): Observable<NurseryModel> {
        return this.remoteRepository.update("nurseries/" + id, body)
            .pipe(
                map((data) => new NurseryModel(data as JSON))
            );
    }

    public deleteNursery(id: string): Observable<{}> {
        return this.remoteRepository.delete("nurseries/" + id);
    }

    public duplicateNursery(nurseryUUID: string): Observable<NurseryModel> {
        return this.remoteRepository.post("nurseries/" + nurseryUUID, null)
            .pipe(
                map((data) => new NurseryModel(data as JSON))
            );
    }

    public addPhotospheres(body: FormData, nurseryUUID: string): Observable<{}> {
        return this.remoteRepository.postWithProgress("nurseries/" + nurseryUUID + "/photospheres/images", body);
    }

    public addCSV(body: FormData, nurseryUUID: string): Observable<{}> {
        return this.remoteRepository.post("nurseries/" + nurseryUUID + "/photospheres/csv", body);
    }

    public connectPhotospheres(nurseryUUID: string): Observable<{}> {
        return this.remoteRepository.post("nurseries/" + nurseryUUID + "/photospheres/connect", null);
    }

    public getPolygons(pageSize: number, page: number, nurseryUUID?: string): Observable<PolygonList> {
        let url = "polygons";
        url += "?size=" + pageSize + "&page=" + page;
        if(nurseryUUID) {
            url += "&search=nursery_uuid:" + nurseryUUID;
        }
        return this.remoteRepository.get(url)
            .pipe(
                map((poly) => new PolygonList(poly as JSON))
            );
    }

    public addPolygon(polygon: PolygonType): Observable<PolygonModel> {
        return this.remoteRepository.post("polygons", polygon)
            .pipe(
                map((poly) => new PolygonModel(poly as JSON))
            );
    }

    public editPolygon(polygonUUID: string, polygon: PolygonType): Observable<PolygonModel> {
        return this.remoteRepository.update("polygons/" + polygonUUID, polygon)
            .pipe(
                map((poly) => new PolygonModel(poly as JSON))
            );
    }

    public deletePolygon(polygonUUID: string): Observable<{}> {
        return this.remoteRepository.delete("polygons/" + polygonUUID);
    }

    public getShortcuts(pageSize: number, page: number, nurseryUUID?: string): Observable<ShortcutList> {
        let url = "shortcuts";
        url += "?size=" + pageSize + "&page=" + page;
        if(nurseryUUID) {
            url += "&search=nursery:" + nurseryUUID;
        }
        return this.remoteRepository.get(url)
            .pipe(
                map((shorts) => new ShortcutList(shorts as JSON))
            );
    }

    public addShortcut(shortcut: ShortcutType): Observable<ShortcutModel> {
        return this.remoteRepository.post("shortcuts", shortcut)
            .pipe(
                map((short) => new ShortcutModel(short as JSON))
            );
    }

    public editShortcut(shortcutUUID: string, shortcut: ShortcutType): Observable<ShortcutModel> {
        return this.remoteRepository.update("shortcuts/" + shortcutUUID, shortcut)
            .pipe(
                map((short) => new ShortcutModel(short as JSON))
            );
    }

    public deleteShortcut(shortcutUUID: string): Observable<{}> {
        return this.remoteRepository.delete("shortcuts/" + shortcutUUID);
    }
}
